import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Copy,
  KeyRound,
  MessageSquare,
  Plus,
  RotateCw,
  Server,
  ShieldAlert,
  Settings as SettingsIcon,
  ShieldCheck,
  Shuffle,
  Snowflake,
  Star,
  Trash2,
  Unlink,
  Wallet,
} from 'lucide-react';
import { adminAccountMode, adminEstimationFreeze, adminMainAccount, adminSettings, adminSingleNet, adminTotp } from '@/api/admin';
import { Switch } from '@/components/ui/switch';
import { env } from '@/lib/env';
import type { AdminAccount, NotificationTemplate } from '@/api/extras';
import type { MainAccountCandidate, MatchingMode, TotpSetupResponse } from '@/api/types';
import { QrCanvas } from '@/components/widgets/QrCanvas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { fmtDateTime, fmtMoney } from '@/lib/format';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { cn } from '@/lib/utils';

export function Settings() {
  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
          <SettingsIcon className="h-5 w-5 text-brand" />
          系统设置
        </h1>
        <p className="text-sm text-ink-2 mt-1">
          管理员账号 · TOTP 绑定 · IP 白名单 · 通知模板 · 系统版本(§9.16)
        </p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MainAccountConfigSection />
        <AccountModeSection />
      </section>

      <EstimationFreezeSection />

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AdminAccountsSection />
        <TotpSection />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <IpWhitelistSection />
        <SystemInfoSection />
      </section>

      <NotificationTemplatesSection />
    </div>
  );
}

// ─── 管理员账号 ─────────────────────────────────────────────────

function AdminAccountsSection() {
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['admin', 'admins'], queryFn: adminSettings.listAdmins });
  const [creating, setCreating] = useState(false);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');

  // 重置密码:二次确认目标 + 成功后展示临时密码(仅此一次)
  const [resetTarget, setResetTarget] = useState<AdminAccount | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const resetPwd = useMutation({
    mutationFn: (id: string) => adminSettings.resetAdminPassword(id),
    onSuccess: (data) => {
      setTempPassword(data.tempPassword);
      setResetTarget(null);
    },
    onError: (err) => toast.error('重置失败', { description: err instanceof ApiError ? err.message : '请稍后重试' }),
  });

  const create = useMutation({
    mutationFn: () =>
      adminSettings.createAdmin({ username: username.trim(), displayName: displayName.trim() || undefined, password: 'temp-' + Math.random().toString(36).slice(2, 8) }),
    onSuccess: () => {
      toast.success('管理员已创建', { description: '初始密码已通过 SMS 推送给该用户,首次登录强制修改' });
      qc.invalidateQueries({ queryKey: ['admin', 'admins'] });
      setUsername('');
      setDisplayName('');
      setCreating(false);
    },
    onError: (err) => toast.error('创建失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminSettings.deleteAdmin(id),
    onSuccess: () => {
      toast.success('管理员已删除');
      qc.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand" />
            管理员账号 · {listQ.data?.length ?? 0}
          </span>
          {!creating && (
            <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              新增
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {creating && (
          <div className="rounded-md border border-line bg-bg-3 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="adm-u" className="text-xs">用户名</Label>
                <Input id="adm-u" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ops-bob" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="adm-n" className="text-xs">显示名(可选)</Label>
                <Input id="adm-n" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Bob 运维" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCreating(false)} disabled={create.isPending}>
                取消
              </Button>
              <Button size="sm" disabled={!username.trim() || create.isPending} onClick={() => create.mutate()}>
                {create.isPending ? '创建中...' : '创建并发送密码'}
              </Button>
            </div>
          </div>
        )}
        {(listQ.data ?? []).map((a) => (
          <div key={a.id} className="flex items-center justify-between rounded-md border border-line bg-bg-3 px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold num">{a.username}</span>
                {a.displayName && <span className="text-xs text-ink-2">{a.displayName}</span>}
                <Pill tone={a.totpEnabled ? 'up' : 'warn'}>{a.totpEnabled ? 'TOTP ✓' : 'TOTP 未绑定'}</Pill>
              </div>
              <div className="text-[11px] text-ink-3 num mt-0.5">
                上次登录 {a.lastLoginAt ? fmtDateTime(a.lastLoginAt) : '—'} · IP {a.lastLoginIp ?? '—'}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setResetTarget(a)} disabled={resetPwd.isPending}>
                <KeyRound className="h-3.5 w-3.5" />
                重置密码
              </Button>
              {a.username !== 'admin' && (
                <Button variant="ghost" size="sm" className="text-down" onClick={() => remove.mutate(a.id)} disabled={remove.isPending}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      {/* ── 重置密码二次确认 Dialog ── */}
      <Dialog open={resetTarget !== null} onOpenChange={(v) => !v && !resetPwd.isPending && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-warn">
              <ShieldAlert className="h-5 w-5" />
              重置管理员密码
            </DialogTitle>
            <DialogDescription>
              将为管理员{' '}
              <strong className="text-ink-0 num">{resetTarget?.username}</strong>
              {resetTarget?.displayName ? `(${resetTarget.displayName})` : ''}{' '}
              生成一个临时密码,原密码立即失效。生成后请妥善转交本人,并要求其首次登录后立即修改。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} disabled={resetPwd.isPending}>
              取消
            </Button>
            <Button
              variant="warn"
              disabled={resetPwd.isPending}
              onClick={() => resetTarget && resetPwd.mutate(resetTarget.id)}
            >
              {resetPwd.isPending ? '重置中…' : '确认重置'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 临时密码展示 Dialog(仅此一次,可复制)── */}
      <Dialog open={tempPassword !== null} onOpenChange={(v) => !v && setTempPassword(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-up-dim">
              <KeyRound className="h-5 w-5" />
              密码已重置 — 请妥善转交
            </DialogTitle>
            <DialogDescription>
              以下临时密码<strong>仅显示这一次</strong>。请通过安全渠道转交给该管理员,并提醒其<strong>首次登录后立即修改密码</strong>。
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1.5 rounded-md border border-line bg-bg-3 p-3">
            <code className="num text-base font-mono text-ink-0 break-all flex-1">{tempPassword}</code>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => tempPassword && copyText(tempPassword, '临时密码已复制')}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPassword(null)}>我已转交</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── TOTP(当前管理员自助绑定/解绑)───────────────────────────────
//
// 真实端点(openapi.yaml /admin/api/v1/me/totp{,/setup,/verify}):
//   GET    /me/totp        → { enabled, enabledAt? }
//   POST   /me/totp/setup  → { secret, otpauthUri }(服务端生成密钥,尚未启用)
//   POST   /me/totp/verify → { enabled, backupCodes[] }(校验首码,通过则落库启用)
//   DELETE /me/totp        → 200(校验当前码/救援码后解绑)
//
// 二维码【本地】渲染(QrCanvas + qrcode 库),密钥与 otpauthUri 绝不外发第三方。

async function copyText(text: string, okMsg: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg);
  } catch {
    toast.error('复制失败', { description: '请手动选中复制' });
  }
}

function TotpSection() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ['admin', 'me', 'totp'], queryFn: adminTotp.status });

  // 绑定流程内的临时态:setup 返回的密钥 + otpauthUri,以及用户输入的首验证码
  const [enroll, setEnroll] = useState<TotpSetupResponse | null>(null);
  const [enrollCode, setEnrollCode] = useState('');

  // 救援码 Dialog(verify 成功后展示,仅此一次)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // 解绑 Dialog
  const [unbindOpen, setUnbindOpen] = useState(false);
  const [unbindCode, setUnbindCode] = useState('');

  const enabled = statusQ.data?.enabled ?? false;
  const enabledAt = statusQ.data?.enabledAt;

  const setup = useMutation({
    mutationFn: adminTotp.setup,
    onSuccess: (data) => {
      setEnroll(data);
      setEnrollCode('');
    },
    onError: (err) => toast.error('无法开始绑定', { description: err instanceof ApiError ? err.message : '请稍后重试' }),
  });

  const verify = useMutation({
    mutationFn: () => adminTotp.verify({ secret: enroll!.secret, code: enrollCode }),
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes ?? []);
      setEnroll(null);
      setEnrollCode('');
      qc.invalidateQueries({ queryKey: ['admin', 'me', 'totp'] });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 401
          ? '验证码错误,请确认 Authenticator 当前 6 位码'
          : err instanceof ApiError
            ? err.message
            : '请稍后重试';
      toast.error('绑定失败', { description: msg });
    },
  });

  const unbind = useMutation({
    mutationFn: () => adminTotp.unbind({ code: unbindCode.trim() }),
    onSuccess: () => {
      toast.success('TOTP 已解绑', { description: '下次登录不再需要验证码;建议尽快重新绑定' });
      setUnbindOpen(false);
      setUnbindCode('');
      qc.invalidateQueries({ queryKey: ['admin', 'me', 'totp'] });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 401
          ? '验证码错误,请输入当前 6 位码或救援码'
          : err instanceof ApiError
            ? err.message
            : '请稍后重试';
      toast.error('解绑失败', { description: msg });
    },
  });

  const enrollValid = /^\d{6}$/.test(enrollCode);
  const unbindValid = unbindCode.trim().length >= 6;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand" />
            TOTP 双因素认证
          </span>
          {statusQ.isLoading ? (
            <Pill tone="neutral">查询中…</Pill>
          ) : enabled ? (
            <Pill tone="up">已绑定</Pill>
          ) : (
            <Pill tone="warn">未绑定</Pill>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {statusQ.isError && (
          <div className="rounded-md border border-down/40 bg-down/10 text-down-dim px-3 py-2 text-xs">
            状态查询失败,请刷新重试。
          </div>
        )}

        {/* ── 已绑定 ── */}
        {!statusQ.isLoading && enabled && (
          <>
            <div className="rounded-md border border-up/40 bg-up/10 text-up-dim px-3 py-2 flex items-start gap-2 text-xs leading-relaxed">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                已启用 TOTP 二次认证{enabledAt ? <>,启用时间 <span className="num">{fmtDateTime(enabledAt)}</span></> : null}。
                管理端登录时需输入 6 位验证码。
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="text-down" onClick={() => setUnbindOpen(true)}>
                <Unlink className="h-3.5 w-3.5" />
                解绑 TOTP
              </Button>
            </div>
          </>
        )}

        {/* ── 未绑定 + 尚未开始绑定 ── */}
        {!statusQ.isLoading && !enabled && !enroll && (
          <>
            <div className="text-xs text-ink-2 leading-relaxed">
              当前账号<strong className="text-warn-dim">未绑定 TOTP</strong>。绑定后管理端登录将要求输入动态验证码。
              点击下方按钮由服务端生成密钥并本地展示二维码。
            </div>
            <Button size="sm" disabled={setup.isPending} onClick={() => setup.mutate()}>
              <ShieldCheck className="h-3.5 w-3.5" />
              {setup.isPending ? '生成中…' : '开始绑定'}
            </Button>
          </>
        )}

        {/* ── 未绑定 + 已 setup,等待扫码 + 输入首码 ── */}
        {!statusQ.isLoading && !enabled && enroll && (
          <div className="flex items-start gap-3">
            <div className="rounded-md border border-line bg-bg-1 p-2 shrink-0">
              {/* 本地渲染,otpauthUri 不外发第三方 */}
              <QrCanvas value={enroll.otpauthUri} size={160} />
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-xs text-ink-2">使用 Google Authenticator / 1Password / Authy 扫码</div>
              <div>
                <div className="text-[11px] uppercase text-ink-3 mb-0.5">无法扫码?手动输入密钥</div>
                <div className="flex items-center gap-1.5">
                  <code className="num text-xs font-mono bg-bg-3 rounded px-2 py-1 break-all flex-1">{enroll.secret}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => copyText(enroll.secret, '密钥已复制')}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="totp-verify" className="text-xs">输入 Authenticator 当前 6 位码以激活</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="totp-verify"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="num text-center tracking-[0.4em]"
                  />
                  <Button size="sm" disabled={!enrollValid || verify.isPending} onClick={() => verify.mutate()}>
                    {verify.isPending ? '验证中…' : '绑定'}
                  </Button>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-ink-3"
                disabled={verify.isPending}
                onClick={() => {
                  setEnroll(null);
                  setEnrollCode('');
                }}
              >
                取消
              </Button>
            </div>
          </div>
        )}

        <div className="text-[11px] text-ink-3 leading-relaxed border-t border-line pt-2">
          ★ TOTP 只作用于管理端登录;强平 / 立即应用 / 紧急停机等操作不再二次确认,仅需填写操作原因并写入审计
        </div>
      </CardContent>

      {/* ── 救援码 Dialog(verify 成功后,仅此一次明文展示)── */}
      <Dialog
        open={backupCodes !== null}
        onOpenChange={(v) => !v && setBackupCodes(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-up-dim">
              <ShieldCheck className="h-5 w-5" />
              TOTP 已绑定 — 请保存救援码
            </DialogTitle>
            <DialogDescription>
              以下救援码<strong>仅显示这一次</strong>。当无法使用 Authenticator 时可用救援码登录或解绑,请妥善离线保存(如打印 / 密码管理器)。
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 rounded-md border border-line bg-bg-3 p-3">
            {(backupCodes ?? []).map((c) => (
              <code key={c} className="num text-sm font-mono text-ink-0 text-center py-1">
                {c}
              </code>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => copyText((backupCodes ?? []).join('\n'), '救援码已复制')}
            >
              <Copy className="h-4 w-4" />
              复制全部
            </Button>
            <Button onClick={() => setBackupCodes(null)}>我已保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 解绑确认 Dialog ── */}
      <Dialog open={unbindOpen} onOpenChange={(v) => !v && !unbind.isPending && setUnbindOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-down">
              <ShieldAlert className="h-5 w-5" />
              解绑 TOTP
            </DialogTitle>
            <DialogDescription>
              解绑后本账号将失去二次认证保护,敏感操作不再要求验证码。请输入当前 6 位验证码或一个救援码确认本人操作。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="totp-unbind-code" className="text-xs">当前验证码 / 救援码</Label>
            <Input
              id="totp-unbind-code"
              inputMode="text"
              placeholder="000000 或救援码"
              value={unbindCode}
              onChange={(e) => setUnbindCode(e.target.value)}
              className="num"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnbindOpen(false)} disabled={unbind.isPending}>
              取消
            </Button>
            <Button variant="warn" disabled={!unbindValid || unbind.isPending} onClick={() => unbind.mutate()}>
              {unbind.isPending ? '解绑中…' : '确认解绑'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── IP 白名单 ─────────────────────────────────────────────────

function IpWhitelistSection() {
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['admin', 'ip-whitelist'], queryFn: adminSettings.listIpWhitelist });
  const [cidr, setCidr] = useState('');
  const [note, setNote] = useState('');

  const add = useMutation({
    mutationFn: () => adminSettings.addIpWhitelist({ cidr: cidr.trim(), note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success('已加入白名单');
      qc.invalidateQueries({ queryKey: ['admin', 'ip-whitelist'] });
      setCidr('');
      setNote('');
    },
    onError: (err) => toast.error('添加失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const remove = useMutation({
    mutationFn: (c: string) => adminSettings.removeIpWhitelist(c),
    onSuccess: () => {
      toast.success('已移除');
      qc.invalidateQueries({ queryKey: ['admin', 'ip-whitelist'] });
    },
  });

  const valid = /^[\d.]+(\/\d{1,2})?$/.test(cidr.trim());

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-4 w-4 text-brand" />
          IP 白名单 · {listQ.data?.length ?? 0}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="ip-cidr" className="text-xs">CIDR</Label>
            <Input
              id="ip-cidr"
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
              placeholder="192.168.1.0/24 或 203.0.113.42/32"
              className="num"
            />
          </div>
          <div className="flex-1 space-y-1">
            <Label htmlFor="ip-note" className="text-xs">备注</Label>
            <Input id="ip-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="办公室 / VPN / CI" />
          </div>
          <Button size="sm" disabled={!valid || add.isPending} onClick={() => add.mutate()}>
            <Plus className="h-4 w-4" />
            添加
          </Button>
        </div>
        <div className="space-y-1.5 pt-2 border-t border-line">
          {(listQ.data ?? []).map((e) => (
            <div key={e.cidr} className="flex items-center justify-between rounded-md bg-bg-3 px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="num font-medium">{e.cidr}</span>
                  {e.note && <span className="text-xs text-ink-2">{e.note}</span>}
                </div>
                <div className="text-[11px] text-ink-3 num mt-0.5">添加于 {fmtDateTime(e.addedAt)}</div>
              </div>
              <Button variant="ghost" size="sm" className="text-down" onClick={() => remove.mutate(e.cidr)} disabled={remove.isPending}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── 系统信息 ─────────────────────────────────────────────────

function SystemInfoSection() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-4 w-4 text-brand" />
          系统版本与运行
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Web 管理端版本" value="@superaios/ctrader-admin v0.1.0" />
        <Row label="后端 API 契约" value="OpenAPI 0.1.0 (docs/api-contract/openapi.yaml)" />
        <Row label="cTrader 协议" value="OpenApi 8.x · ProtoMessageVersion=2" />
        <Row label="Demo 端点" value="demo.ctraderapi.com:5035" />
        <Row label="Live 端点" value={<span className="text-ink-3">live.ctraderapi.com:5035 (Phase 7 启用)</span>} />
        <Row label="时区" value="Asia/Shanghai" />
        <Row
          label="dev mock"
          value={env.useMock ? <Pill tone="warn">已启用 (VITE_USE_MOCK=true)</Pill> : <Pill tone="up">已禁用 · 走真实后端</Pill>}
        />
        <div className="pt-2 mt-2 border-t border-line">
          <Button variant="outline" size="sm" onClick={() => toast.info('运行日志面板待接入')}>
            <RotateCw className="h-3.5 w-3.5" />
            查看运行日志
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── 通知模板 ────────────────────────────────────────────────

function NotificationTemplatesSection() {
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['admin', 'notification-templates'], queryFn: adminSettings.listTemplates });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<NotificationTemplate> }) =>
      adminSettings.updateTemplate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'notification-templates'] });
    },
    onError: (err) => toast.error('更新失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-brand" />
          通知模板 · 飞书机器人
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {(listQ.data ?? []).map((t) => (
          <div key={t.id} className="rounded-md border border-line bg-bg-3 p-3">
            <div className="flex items-center justify-between mb-2">
              <Pill tone={t.enabled ? 'up' : 'neutral'} className="text-xs">{t.event}</Pill>
              <button
                type="button"
                onClick={() => update.mutate({ id: t.id, body: { enabled: !t.enabled } })}
                className={cn(
                  'relative h-5 w-9 rounded-full transition-colors',
                  t.enabled ? 'bg-up' : 'bg-ink-3',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                    t.enabled ? 'translate-x-4' : 'translate-x-0.5',
                  )}
                />
              </button>
            </div>
            <pre className="text-[11px] num bg-bg-1 rounded px-2 py-1.5 whitespace-pre-wrap text-ink-1">{t.template}</pre>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-ink-2">{label}</span>
      <span className="text-sm text-ink-1">{value}</span>
    </div>
  );
}

// ─── 真实账户撮合模式 HEDGING / NETTING ───────────────────────────
//
// **热切换**:保存写 system_config['account.matching_mode'],reconcile 约 3s 内生效,无需重启。
// HEDGING(默认):cTrader 默认账户类型,每单开独立仓,系统减仓走 ClosePositionReq 平真实仓。
// NETTING:每品种轧一个净仓,需先向券商(IC Markets)申请开通;账户未开通就切会累积对冲仓震荡。

const MODE_LABEL: Record<MatchingMode, string> = {
  HEDGING: '对冲(Hedging)',
  NETTING: '净额(Netting)',
};

function AccountModeSection() {
  const qc = useQueryClient();
  const modeQ = useQuery({
    queryKey: ['admin', 'account-mode'],
    queryFn: adminAccountMode.get,
  });
  const current: MatchingMode | undefined = modeQ.data?.mode;

  const [target, setTarget] = useState<MatchingMode | null>(null);
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: (mode: MatchingMode) => adminAccountMode.set({ mode, note: note.trim() || undefined }),
    onSuccess: (resp) => {
      toast.success('已切换撮合模式', {
        description: `${MODE_LABEL[resp.previousMode]} → ${MODE_LABEL[resp.mode]},约 3 秒内生效(无需重启)`,
      });
      setTarget(null);
      setNote('');
      qc.invalidateQueries({ queryKey: ['admin', 'account-mode'] });
    },
    onError: (err) => {
      toast.error('切换失败', {
        description: err instanceof ApiError ? err.message : '未知错误',
      });
    },
  });

  const singleNet = modeQ.data?.singleNetPosition ?? false;
  const saveSingleNet = useMutation({
    mutationFn: (enabled: boolean) => adminSingleNet.set({ enabled }),
    onSuccess: (resp) => {
      toast.success(resp.singleNetPosition ? '已开启单一净仓' : '已关闭单一净仓', {
        description: '约 3 秒内生效(无需重启)',
      });
      qc.invalidateQueries({ queryKey: ['admin', 'account-mode'] });
    },
    onError: (err) => {
      toast.error('切换失败', {
        description: err instanceof ApiError ? err.message : '未知错误',
      });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shuffle className="h-4 w-4 text-brand" />
          真实账户撮合模式
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {/* 当前模式 */}
        <div className="rounded-md border border-line bg-bg-3 px-3 py-2 flex items-center justify-between">
          <span className="text-xs text-ink-2">当前模式</span>
          {modeQ.isLoading ? (
            <span className="text-ink-3">加载中…</span>
          ) : current ? (
            <Pill tone={current === 'HEDGING' ? 'brand' : 'up'}>{MODE_LABEL[current]}</Pill>
          ) : (
            <Pill tone="warn">未知</Pill>
          )}
        </div>

        {/* 说明 */}
        <div className="rounded-md border border-warn/40 bg-warn/10 text-warn-dim px-3 py-2 flex gap-2 items-start text-xs leading-relaxed">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            仅在已向券商申请开通 <strong>净额(Netting)</strong> 并确认通过后再切到净额。
            账户实际仍是对冲时切净额,系统的反向对冲单不会被轧平,会累积对冲仓位震荡。
            热切换约 3 秒内生效,无需重启。
          </div>
        </div>

        {/* 模式选择 */}
        <div className="space-y-1.5">
          <Label htmlFor="acct-mode-select" className="text-xs">
            切换到
          </Label>
          <select
            id="acct-mode-select"
            value={target ?? current ?? 'HEDGING'}
            onChange={(e) => setTarget(e.target.value as MatchingMode)}
            disabled={modeQ.isLoading}
            className="flex h-9 w-full items-center rounded-md border border-line bg-bg-3 px-3 py-2 text-sm text-ink-1 focus:outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="HEDGING">{MODE_LABEL.HEDGING}（默认）</option>
            <option value="NETTING">{MODE_LABEL.NETTING}（需券商开通）</option>
          </select>
        </div>

        {/* 备注 */}
        <div className="space-y-1.5">
          <Label htmlFor="acct-mode-note" className="text-xs">
            操作备注(可选,记入审计)
          </Label>
          <Input
            id="acct-mode-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="例如:IC Markets 已开通 netting,切换至净额模式"
          />
        </div>

        {/* 保存 */}
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            disabled={!current || (target ?? current) === current || save.isPending}
            onClick={() => save.mutate((target ?? current) as MatchingMode)}
          >
            {save.isPending ? '切换中…' : '切换模式'}
          </Button>
        </div>

        {/* 单一净仓开关(positionId) */}
        <div className="border-t border-line pt-3 mt-1 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-1">开启单一净仓</span>
              <Pill tone={singleNet ? 'up' : 'neutral'}>{singleNet ? '已开启' : '已关闭'}</Pill>
            </div>
            <Switch
              checked={singleNet}
              disabled={modeQ.isLoading || saveSingleNet.isPending}
              onCheckedChange={(v) => saveSingleNet.mutate(v)}
              aria-label="开启单一净仓"
            />
          </div>
          <div className="rounded-md border border-warn/40 bg-warn/10 text-warn-dim px-3 py-2 text-xs leading-relaxed">
            开启后(仅<strong>对冲(Hedging)</strong>模式下生效):每品种用 positionId 维护成<strong>一笔净仓</strong>
            （同向加仓并入同仓 / 反向翻转先平后开），而非每单开独立仓。
            启用前请在真实账户实测(同向加量是否真合并、翻转两步、是否频繁 POSITION_LOCKED);出问题关闭即秒级回滚。
            约 3 秒内生效,无需重启。
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── N4 估值失配冻结(§7.3)─────────────────────────────────────
//
// 某品种估值引擎盈亏与真实净仓权威盈亏严重背离(连续超冻结带)→ 系统冻结该品种用户侧自动处置
// (自动爆仓锁定 + 用户平仓结算),对冲照常跑。管理员核对账本无误后在此解除。

function EstimationFreezeSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'estimation-freeze'],
    queryFn: adminEstimationFreeze.list,
    refetchInterval: 15000,
  });
  const items = q.data?.items ?? [];

  const clear = useMutation({
    mutationFn: (symbolId: string) => adminEstimationFreeze.clear(symbolId, '管理员核对账本后解除'),
    onSuccess: (resp) => {
      toast.success('已解除冻结', { description: `品种 ${resp.symbolId};若基差仍超带会被重新冻结` });
      qc.invalidateQueries({ queryKey: ['admin', 'estimation-freeze'] });
    },
    onError: (err) => {
      toast.error('解除失败', { description: err instanceof ApiError ? err.message : '未知错误' });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Snowflake className="h-4 w-4 text-brand" />
          估值失配冻结
          {items.length > 0 && <Pill tone="down">{items.length} 个品种冻结中</Pill>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="rounded-md border border-line bg-bg-3 px-3 py-2 text-xs text-ink-2 leading-relaxed">
          某品种估值与真实净仓盈亏严重背离时,系统自动冻结其用户侧自动处置(自动结算锁定 / 用户平仓),
          对冲照常运行。请核对该品种账本无误后再解除;若背离仍在,解除后会被自动重新冻结。
        </div>
        {q.isLoading ? (
          <div className="text-ink-3">加载中…</div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-line bg-bg-3 px-3 py-3 text-center text-ink-3">
            当前无冻结品种
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const basisUsd =
                it.detail?.basisCents != null ? (it.detail.basisCents / 100).toFixed(2) : null;
              return (
                <div
                  key={it.symbolId}
                  className="flex items-center justify-between rounded-md border border-down/40 bg-down/5 px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-ink-0">
                      {it.symbolName || `#${it.symbolId}`}
                    </span>
                    {basisUsd != null && (
                      <span className="text-xs text-ink-3 num">基差 ${basisUsd}</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={clear.isPending}
                    onClick={() => clear.mutate(it.symbolId)}
                  >
                    解除冻结
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── 主账户配置 ─────────────────────────────────────────────────
//
// 注意:**不是热切换**。保存只把目标账户 id 写入 system_config + 审计日志,
// 实际跟单仍使用启动期注入的主账户 id,**必须重启后端服务**才会用新主账户。
// 入口放在 Settings 顶部:运维场景里这是个低频但风险操作。

function MainAccountConfigSection() {
  const cfgQ = useQuery({
    queryKey: ['admin', 'main-account', 'config'],
    queryFn: adminMainAccount.getConfig,
  });
  const cfg = cfgQ.data;
  const candidates: MainAccountCandidate[] = cfg?.candidates ?? [];
  const currentId = cfg?.currentMainAccountId && cfg.currentMainAccountId !== '0' ? cfg.currentMainAccountId : '';
  const currentLogin = cfg?.currentMainAccountLogin || '';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-brand" />
          主账户配置(只读)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="rounded-md border border-line bg-bg-3 px-3 py-2 flex gap-2 items-start text-xs leading-relaxed text-ink-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-brand" />
          <div>
            切换主真实账号请前往「cTID 与连接」页选择 LIVE 账号 —— 该流程带 LIVE 校验、TOTP、切换前预检与重启复检闭环。
            本卡片仅只读展示当前配置。
          </div>
        </div>

        {/* 当前主账户 */}
        <div className="rounded-md border border-line bg-bg-3 px-3 py-2 flex items-center justify-between">
          <span className="text-xs text-ink-2">当前主账户</span>
          {cfgQ.isLoading ? (
            <span className="text-ink-3">加载中…</span>
          ) : currentId ? (
            <span className="flex items-center gap-2">
              <Pill tone="brand" className="gap-1">
                <Star className="h-3 w-3" />
                主账户
              </Pill>
              <span className="num text-ink-0 font-medium">{currentLogin || '—'}</span>
              <span className="text-xs text-ink-3 num">({currentId})</span>
            </span>
          ) : (
            <Pill tone="warn">未配置</Pill>
          )}
        </div>

        {/* 已同步候选账户(只读) */}
        {candidates.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs text-ink-3">已同步候选账户</div>
            <div className="space-y-1">
              {candidates.map((c) => (
                <div
                  key={c.ctidTraderAccountId}
                  className="rounded-md bg-bg-3 px-3 py-2 flex items-center justify-between text-xs gap-2"
                >
                  <span className="num text-ink-1 truncate">
                    {c.accountLogin} <span className="text-ink-3">({c.ctidTraderAccountId})</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="num text-ink-3">余额 {fmtMoney(c.balance)}</span>
                    <Pill tone={c.connectionStatus === 'connected' ? 'up' : 'neutral'}>
                      {c.connectionStatus === 'connected' ? '已连接' : '未连接'}
                    </Pill>
                    {c.ctidTraderAccountId === currentId && <Pill tone="brand">当前</Pill>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
